import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { apiClient, AuthResponse } from "@/lib/api";
import { getErrorMessage, getLoginUrl } from "@/lib/utils";
import { FORCE_LOGOUT_REASON_KEY } from "@/lib/socket";
import { useForceLogoutSocket } from "@/hooks/useForceLogoutSocket";
import { toast } from "sonner";

interface AuthUser {
  id: string;
  email: string;
  firstLogin?: boolean;
  profile?: {
    id: string;
    name: string;
    position?: string;
    avatarUrl?: string;
    isActive: boolean;
  };
  roles: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAdmin: boolean;
  isAgent: boolean;
  isTeamLeader: boolean;
  isTechTeam: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; firstLogin?: boolean }>;
  signOut: () => Promise<void>;
  changePassword: (
    email: string,
    password: string,
    confirmPassword: string,
  ) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated and fetch user data
  const fetchUserData = async () => {
    if (!apiClient.isAuthenticated()) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const userData = await apiClient.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      // If token is invalid, clear it and redirect to login
      await apiClient.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const signIn = async (
    email: string,
    password: string,
  ): Promise<{ error: Error | null; firstLogin?: boolean }> => {
    try {
      setIsLoading(true);
      const authResponse = await apiClient.login({ email, password });
      setUser(authResponse.user);
      toast.success("Signed in successfully");
      return { error: null, firstLogin: authResponse.user.firstLogin };
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to sign in");
      const isDeactivated = /deactivated|inactive|account\s*(is\s*)?disabled/i.test(errorMessage);
      const displayMessage = isDeactivated
        ? "Account is Deactivated please contact your admin"
        : errorMessage;
      toast.error(displayMessage);
      return { error: new Error(displayMessage) };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      await apiClient.logout();
      setUser(null);
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Sign out error:", error);
      // Clear user state even if logout fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (
    email: string,
    password: string,
    confirmPassword: string,
  ) => {
    try {
      await apiClient.changePassword(email, password, confirmPassword);
      toast.success("Password updated successfully");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to change password"));
      throw error;
    }
  };

  const refreshUser = async () => {
    if (!apiClient.isAuthenticated()) {
      setUser(null);
      return;
    }

    try {
      const userData = await apiClient.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error("Error refreshing user data:", error);
      // If refresh fails, user might need to re-authenticate
      await signOut();
    }
  };

  // Computed properties
  const isAdmin = user?.roles?.includes("admin") || false;
  const isAgent = user?.roles?.includes("agent") || false;
  const isTeamLeader = user?.roles?.includes("team_leader") || false;
  const isTechTeam = isAdmin || isAgent || isTeamLeader;

  // Socket: when admin deactivates this user, server sends force_logout
  const forceLogoutHandledRef = useRef(false);
  useForceLogoutSocket(user?.id ?? null, (reason: string, disconnect: () => void) => {
    if (forceLogoutHandledRef.current) return;
    forceLogoutHandledRef.current = true;
    // Disconnect socket first so we don't receive the event again
    disconnect();
    // Clear tokens synchronously before redirect to prevent infinite reload loop
    apiClient.clearTokensOnly();
    setUser(null);
    try {
      sessionStorage.setItem(FORCE_LOGOUT_REASON_KEY, reason);
    } catch {
      // ignore
    }
    window.location.href = getLoginUrl();
  });

  const value: AuthContextType = {
    user,
    isLoading,
    isAdmin,
    isAgent,
    isTeamLeader,
    isTechTeam,
    signIn,
    signOut,
    changePassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
